/**
 * recurring-task-derive.ts — D27 (P2-05): lazy materialization of recurring task instances.
 *
 * A "series parent" is any Task with a `recurrence` RRULE string and no `recurrenceParentId`.
 * The parent itself IS the first occurrence (on its own dueDate). This module generates the
 * concrete child instances for the upcoming window, reusing the existing `expandRecurrence`
 * engine (DRY — no second RRULE implementation).
 *
 * Idempotency / sync-safety (D31): instance ids are DETERMINISTIC (`recurrenceInstanceId`).
 * Two devices generating the same occurrence produce the same id, so the remote merge dedups
 * them instead of creating duplicates.
 */

import type { CalendarEvent, Task } from "../types";
import { addDaysIso, todayIso } from "../lib/date";
import { expandRecurrence, parseRrule } from "../lib/recurrence";

/** Default look-ahead window (days) — caps how many future instances we materialize. */
export const RECURRENCE_WINDOW_DAYS = 90;

/** Deterministic id for a generated instance — stable across devices/runs. */
export function recurrenceInstanceId(parentId: string, dateIso: string): string {
  return `${parentId}__inst__${dateIso}`;
}

/** A series parent = has recurrence, is not itself a generated instance. */
export function isRecurrenceParent(task: Task): boolean {
  return Boolean(task.recurrence && !task.recurrenceParentId);
}

/** Occurrence dates for a parent within [today, today+windowDays], excluding the parent's own date. */
export function parentOccurrenceDates(
  parent: Task,
  today = todayIso(),
  windowDays = RECURRENCE_WINDOW_DAYS,
): string[] {
  if (!parent.recurrence || !parseRrule(parent.recurrence)) return [];
  // Inclusive range spanning exactly `windowDays` days: [today, today + windowDays - 1].
  const rangeEnd = addDaysIso(windowDays - 1, new Date(`${today}T00:00:00`));
  // Adapt the task to the CalendarEvent shape expandRecurrence expects.
  const pseudo: CalendarEvent = {
    id: parent.id,
    title: parent.title,
    date: parent.dueDate,
    time: "00:00",
    rrule: parent.recurrence,
  };
  // Exdates = occurrences the user deleted; excluding them here means the sweep neither
  // regenerates them (materialize) nor keeps stale instances on those dates (orphan prune).
  const exdates = new Set(parent.recurrenceExdates ?? []);
  return expandRecurrence(pseudo, today, rangeEnd).filter(
    (date) => date !== parent.dueDate && !exdates.has(date),
  );
}

/** Build a fresh child instance Task from a parent for a given occurrence date. */
function buildInstance(parent: Task, date: string, nowIso: string): Task {
  return {
    id: recurrenceInstanceId(parent.id, date),
    title: parent.title,
    dueDate: date,
    status: "todo",
    priority: parent.priority,
    estimateMinutes: parent.estimateMinutes,
    subtasks: parent.subtasks.map((st) => ({ ...st, done: false })),
    ...(parent.projectId ? { projectId: parent.projectId } : {}),
    ...(parent.goalId ? { goalId: parent.goalId } : {}),
    ...(parent.description ? { description: parent.description } : {}),
    ...(parent.tags && parent.tags.length ? { tags: [...parent.tags] } : {}),
    recurrenceParentId: parent.id,
    recurrenceKey: `${parent.id}:${date}`,
    createdAt: nowIso,
  };
}

/**
 * New instances to ADD for one parent within the window, skipping any whose id already exists.
 * Pure: pass `existingIds` (all current task ids) for dedup, `now` for deterministic tests.
 */
export function materializeTaskInstances(
  parent: Task,
  existingIds: Set<string>,
  today = todayIso(),
  windowDays = RECURRENCE_WINDOW_DAYS,
  nowIso = new Date().toISOString(),
): Task[] {
  if (!isRecurrenceParent(parent)) return [];
  return parentOccurrenceDates(parent, today, windowDays)
    .filter((date) => !existingIds.has(recurrenceInstanceId(parent.id, date)))
    .map((date) => buildInstance(parent, date, nowIso));
}

/**
 * Ids of FUTURE, UNDONE instances of a parent that no longer match its current occurrence set
 * (e.g. after the series pattern/dueDate is edited). Past or completed instances are preserved.
 */
export function orphanedInstanceIds(
  parent: Task,
  allTasks: Task[],
  today = todayIso(),
  windowDays = RECURRENCE_WINDOW_DAYS,
): string[] {
  const valid = new Set(parentOccurrenceDates(parent, today, windowDays));
  // An instance the user RESCHEDULED (dueDate differs from its original
  // occurrence date in recurrenceKey) is user-owned — never prune it, even
  // though its new date doesn't match the pattern.
  const originalDate = (t: Task) =>
    t.recurrenceKey?.startsWith(`${parent.id}:`) ? t.recurrenceKey.slice(parent.id.length + 1) : t.dueDate;
  return allTasks
    .filter(
      (t) =>
        t.recurrenceParentId === parent.id &&
        t.status !== "done" &&
        t.dueDate > today &&
        t.dueDate === originalDate(t) &&
        !valid.has(t.dueDate),
    )
    .map((t) => t.id);
}

/** Result of sweeping all series parents: instances to add + stale instance ids to remove. */
export type RecurringSweep = { toAdd: Task[]; toRemoveIds: string[] };

/** Sweep every series parent in `tasks`, returning the net add/remove plan (pure). */
export function sweepRecurringTasks(
  tasks: Task[],
  today = todayIso(),
  windowDays = RECURRENCE_WINDOW_DAYS,
  nowIso = new Date().toISOString(),
): RecurringSweep {
  const existingIds = new Set(tasks.map((t) => t.id));
  const toAdd: Task[] = [];
  const toRemoveIds: string[] = [];
  for (const parent of tasks.filter(isRecurrenceParent)) {
    for (const instance of materializeTaskInstances(parent, existingIds, today, windowDays, nowIso)) {
      existingIds.add(instance.id);
      toAdd.push(instance);
    }
    toRemoveIds.push(...orphanedInstanceIds(parent, tasks, today, windowDays));
  }
  return { toAdd, toRemoveIds };
}
